// URL-based state management
// Replaces Svelte stores with URL parameter management

export interface URLState {
	from: string;
	until: string;
	trefwoordId?: number;
	search?: string;
}

// Parse current URL and return state object
export function parseURLState(): URLState {
	const url = new URL(window.location.href);
	const pathname = url.pathname;
	const params = url.searchParams;

	// Default values
	let from = params.get('from') || '';
	let until = params.get('until') || '';
	let trefwoordId: number | undefined;
	let search: string | undefined;

	// Parse different route patterns
	if (pathname.startsWith('/summary/')) {
		const parts = pathname.split('/');
		if (parts.length >= 4) {
			from = parts[2] || '';
			until = parts[3] || '';
		}
	} else if (pathname.startsWith('/pertrefwoord/')) {
		const parts = pathname.split('/');
		if (parts.length >= 3) {
			trefwoordId = parseInt(parts[2]) || undefined;
		}
	} else if (pathname.startsWith('/search/')) {
		const parts = pathname.split('/');
		if (parts.length >= 3) {
			search = decodeURIComponent(parts[2]) || undefined;
		}
	}

	// Override with URL params if present
	if (params.get('trefwoordId')) {
		trefwoordId = parseInt(params.get('trefwoordId')!) || undefined;
	}
	if (params.get('q')) {
		search = params.get('q')!;
	}

	return { from, until, trefwoordId, search };
}

// Update URL with new state - simplified
export function updateURLState(newState: Partial<URLState>, replace = false) {
	const state = { ...parseURLState(), ...newState };

	// Simple path determination
	let newPath = '/';
	if (state.trefwoordId !== undefined) {
		newPath = `/pertrefwoord/${state.trefwoordId}`;
	} else if (state.search) {
		newPath = `/search/${encodeURIComponent(state.search)}`;
	} else if (state.from && state.until) {
		newPath = `/summary/${state.from}/${state.until}`;
	}

	// Add query parameters only if needed
	const params = new URLSearchParams();
	if (state.from) params.set('from', state.from);
	if (state.until) params.set('until', state.until);
	if (state.search) params.set('q', state.search);

	const queryString = params.toString();
	const newUrl = queryString ? `${newPath}?${queryString}` : newPath;

	// Update browser history
	const method = replace ? 'replaceState' : 'pushState';
	window.history[method]({}, '', newUrl);
}

// Navigate to a specific route
export function navigateTo(path: string, state?: Partial<URLState>) {
	let finalUrl = path;
	
	if (state) {
		// Add query parameters to the provided path
		const params = new URLSearchParams();
		if (state.from) params.set('from', state.from);
		if (state.until) params.set('until', state.until);
		if (state.search) params.set('q', state.search);
		if (state.trefwoordId !== undefined) params.set('trefwoordId', state.trefwoordId.toString());
		
		const queryString = params.toString();
		finalUrl = queryString ? `${path}?${queryString}` : path;
	}
	
	window.history.pushState({}, '', finalUrl);

	// Dispatch a custom event for components to listen to
	window.dispatchEvent(new PopStateEvent('popstate'));
}

// Get current route name
export function getCurrentRoute(): string {
	const pathname = window.location.pathname;

	if (pathname === '/' || pathname.startsWith('/summary/')) {
		return 'summary';
	} else if (pathname.startsWith('/pertrefwoord/')) {
		return 'pertrefwoord';
	} else if (pathname.startsWith('/search/')) {
		return 'search';
	} else if (pathname.startsWith('/importeer')) {
		return 'importeer';
	}

	return 'summary'; // default
}
